import { ApplicationIdentifier, APPLICATION_IDENTIFIERS_MAP } from './ApplicationIdentifiers';

const GROUP_SEPARATOR = String.fromCharCode(29);
const CAPTURE_PARENTHESIS = /\((\d+)\)([^()]*?)(?=\(|$)/g;

/**
 * Represents an Application Identifier and its corresponding data element
 */
interface IGS1Fragment {
    readonly code: string;
    readonly value: string;
}

/*
 * This class parses GS1-128 barcodes and returns an object with an array of sub-codes and values
 *
 * The GS1-128 barcode is an aggregation of multiple data elements separated by application identifiers (AI).
 * To extract the data, the barcode must be split into its components based on the AI and
 * their expected value lengths. Some codes have varying value length.
 */
export class GS1CodeParser {
    /*
     * Parses a GS1-128 barcodes and returns an object with an array of sub-codes and values
     *
     * GS1-128 codes use the ASCII group separator to mark the end of a variable length AI code
     * This splits the code on the group separator to guarantee variable length codes are always at the end of a string
     */
    public static parse(gs1Code: string): ParsedGS1Code {
        let parsedValues: IGS1Fragment[];

        // Attempt to parse using the group separator
        try {
            parsedValues = [].concat.apply(
                [],
                // @ts-ignore strictBindCallApply fixme?
                gs1Code.split(GROUP_SEPARATOR).map((val) => this.parseApplicationIdentifiers(val)),
            );
            return new ParsedGS1Code(parsedValues);
        } catch (err) {
            parsedValues = [];
            console.debug(err.message);
            console.debug(`Failed to parse gs1Code using group separator: ${gs1Code}`);
        }

        // Attempt to parse by capturing AIs surrounded by parenthesis
        try {
            parsedValues = this.parseParenthesisSurroundedApplicationIdentifiers(gs1Code);
        } catch (err) {
            parsedValues = [];
            console.debug(err.message);
            console.debug(`Failed to parse gs1Code using parenthesis: ${gs1Code}`);
        }

        return new ParsedGS1Code(parsedValues);
    }

    /**
     * Attempts to parse a GS1-128 barcode in which the application identifiers
     * are surrounded by parenthensis (Ex. (01)10850046126007(17)250527(10)242630016).
     * Throws an error if the captured application identifier does not exist in the known AIs.
     *
     * @param gs1Code {string}
     * @returns {GS1Fragment[]} An array of GS1Fragments
     */
    private static parseParenthesisSurroundedApplicationIdentifiers(gs1Code: string): IGS1Fragment[] {
        const values: IGS1Fragment[] = [...gs1Code.matchAll(CAPTURE_PARENTHESIS)].map((match) => {
            const [_, applicationIdentifier, dataElement] = match;

            const combinedString = applicationIdentifier + dataElement;
            const parsedAI = this.findApplicationIdentifier(combinedString);

            if (!parsedAI) {
                throw new Error(`Unsupported AI found while parsing ${applicationIdentifier}`);
            }

            return this.decodeAiValue(parsedAI, combinedString);
        });

        return values;
    }

    /*
     * This method looks for a valid AI code in the input string and stores the decoded AI value
     *
     * If the found AI code's total length is larger than the input string, the string is split and
     * this method is called recursively with the new substring to extract further AI codes within it
     * If no valid AI is found, it throws an error caught by the parent method to fail as the GS1-Code is invalid
     */
    private static parseApplicationIdentifiers(gs1CodeSection: string): IGS1Fragment[] {
        let values = Array<IGS1Fragment>();
        const applicationIdentifier = this.findApplicationIdentifier(gs1CodeSection);

        if (!applicationIdentifier) {
            // If no application identifier found this is not a valid GS1 Code
            throw new Error(`Supported AI not found in section ${gs1CodeSection}.  Invalid GS1 Code`);
        }

        // If this code section is longer than the AI's total length, it contains more AIs to be parsed
        if (gs1CodeSection.length > applicationIdentifier.getTotalLength()) {
            values = values.concat(this.parseApplicationIdentifiers(gs1CodeSection.substring(applicationIdentifier.getTotalLength())));
        }

        values.push(this.decodeAiValue(applicationIdentifier, gs1CodeSection));

        return values;
    }

    /*
     * This method looks for an AI code within the input string by comparing the first 2-4 digits with the
     * defined currently supported AIs.
     */
    private static findApplicationIdentifier(value: string): ApplicationIdentifier {
        let codeLength = 2;
        while (codeLength < 5) {
            const code = value.substring(0, codeLength);
            const applicationIdentifier = APPLICATION_IDENTIFIERS_MAP.get(code);
            if (applicationIdentifier) {
                return applicationIdentifier;
            }

            codeLength++;
        }

        return null;
    }

    /*
     * This method extracts the AI code value by parsing out the AI code length
     * In the case of a fractional AI code, the digit between the code and code value is used to create a decimal number
     */
    private static decodeAiValue(applicationIdentifier: ApplicationIdentifier, input: string): IGS1Fragment {
        let value: any;

        // Fractional AIs contain a digit between the AI code and value that defines the number of decimal places
        // Example: 320 0 00345 = 345 lbs, but 320 2 00345 = 3.45 lbs
        if (applicationIdentifier.isFractional()) {
            const scale = Number(input.charAt(applicationIdentifier.getCode().length));
            const num = input.substring(applicationIdentifier.getCode().length + 1, applicationIdentifier.getTotalLength());

            value = Number(num) / Math.pow(10, scale);
        } else {
            value = input.substring(applicationIdentifier.getCode().length, applicationIdentifier.getTotalLength());
        }

        return {
            code: applicationIdentifier.getCode(),
            value,
        };
    }
}

/*
 * This class holds an array of maps of AI code to AI value contained in the parsed GS1-128 code
 */
export class ParsedGS1Code {
    private readonly values: any;

    constructor(values: any) {
        this.values = values;
    }

    /*
     * This method looks for the AI code value for a given AI code within its stored mappings
     */
    public getValueForApplicationIdentifier(applicationIdentifierCode: string) {
        if (this.values !== undefined && this.values instanceof Array) {
            const result = this.values.filter((r) => r.code === applicationIdentifierCode);
            if (result[0]) {
                return result[0].value;
            }
        }
    }
}
